import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  List,
  ListItemText,
  ListItemButton,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import MapsUgcOutlinedIcon from '@mui/icons-material/MapsUgcOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { Conversation } from '../../models/Message';
import { formatRelativeDateTime } from '../../utils/dateUtils';
import styles from './History.module.scss';
import ConversationMenu from './ConversationMenu';
import { colors } from '../../styles/theme';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { RootState, AppDispatch } from '../../redux/store';
import { fetchMoreConversations } from '../../redux/slices/chat';
import { getValidToken } from '../../redux/slices/userSlice';
import { handleTokenError } from '../../utils/tokenErrorHandler';
import { isLegacyConversationType } from '../../redux/slices/chat/chatConfig';

interface HistoryProps {
  isHistoryOpenMobile: boolean;
  handleNewChat: () => void;
  conversations: Conversation[];
  selectedSession: Conversation | null;
  handleSelectSession: (conversation: Conversation) => void;
  setIsHistoryOpenMobile: (isOpen: boolean) => void;
  handleDeleteConversation: (conversationId: string, thinkingType: 'reflection' | 'fraktal' | 'prompt-graph') => void;
  loadingHistory: boolean;
}


const History: React.FC<HistoryProps> = ({
  isHistoryOpenMobile,
  handleNewChat,
  conversations,
  selectedSession,
  handleSelectSession,
  setIsHistoryOpenMobile,
  handleDeleteConversation,
  loadingHistory
}) => {
  // Add translation hook
  const { t } = useUntypedTranslation();
  
  // Add Redux hooks
  const dispatch = useDispatch<AppDispatch>();
  const { hasMore, loadingMore } = useSelector((state: RootState) => state.chat.pagination);
  const { user } = useSelector((state: RootState) => state.user);
  
  // State for context menu
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    conversationId: string;
    thinkingType: 'reflection' | 'fraktal' | 'prompt-graph';
  } | null>(null);
  
  // State for delete confirmation dialog
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<{
    id: string;
    thinkingType: 'reflection' | 'fraktal' | 'prompt-graph';
  } | null>(null);
  
  // Prevent initial animation flicker with stabilized mounting
  const [isPanelMounted, setIsPanelMounted] = useState(false);
  
  // Use a callback ref instead of useRef for better compatibility with MUI
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
  
  // Only scroll to top when we explicitly select a new conversation
  const [shouldScrollToTop, setShouldScrollToTop] = useState(false);

  // Handle component mounting
  useEffect(() => {
    // Set the panel as mounted after a short delay
    const timer = setTimeout(() => {
      setIsPanelMounted(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);


  // Only scroll to top when explicitly selecting a conversation
  useEffect(() => {
    // Reset position when explicitly requested
    if (shouldScrollToTop && listElement) {
      // Use a small delay to prevent layout jumps
      const timer = setTimeout(() => {
        listElement.scrollTop = 0;
        setShouldScrollToTop(false);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [shouldScrollToTop, listElement]);

  // Handle right-click
  const handleContextMenu = (event: React.MouseEvent, conversation: Conversation) => {
    event.preventDefault();
    event.stopPropagation();
    
    // For mobile, don't close the history panel when opening context menu
    if (window.innerWidth <= 768) {
      event.nativeEvent.stopImmediatePropagation(); // Prevent any parent handlers from firing
    }
    
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            conversationId: conversation.id,
            thinkingType: conversation.thinkingType
          }
        : null,
    );
  };

  // Handle closing the context menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Show delete confirmation when selecting Delete from context menu
  const handleDeleteFromContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (contextMenu) {
      event.stopPropagation(); // Prevent conversation selection
      
      setConversationToDelete({
        id: contextMenu.conversationId,
        thinkingType: contextMenu.thinkingType
      });
      setConfirmDeleteOpen(true);
      setContextMenu(null);
    }
  };
  
  // Cancel delete
  const handleCancelDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection
    setConfirmDeleteOpen(false);
    setConversationToDelete(null);
  };

  // Confirm delete
  const handleConfirmDelete = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent conversation selection
    if (conversationToDelete) {
      handleDeleteConversation(conversationToDelete.id, conversationToDelete.thinkingType);
    }
    setConfirmDeleteOpen(false);
    setConversationToDelete(null);
  };

  // Enhanced conversation selection handler
  const enhancedHandleSelectSession = useCallback((conversation: Conversation) => {
    setShouldScrollToTop(true); // Request scroll to top
    handleSelectSession(conversation);
    setIsHistoryOpenMobile(false);
  }, [handleSelectSession, setIsHistoryOpenMobile]);


  // Updated LoadMoreButton to use Redux pagination state
  const LoadMoreButton = ({ onClick }: { onClick: () => void }) => {
    const handleClick = useCallback((e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();   // Prevent default behavior of the button, otherwise it will close the panel
      e.stopPropagation();
      //console.log('LoadMore button clicked/touched');
      onClick();
    }, [onClick]);

    return (
      <Box 
        sx={{ textAlign: 'center', p: 2 }}
        onClick={(e) => e.stopPropagation()} // Prevent any clicks in this area from reaching overlay
        onTouchEnd={(e) => e.stopPropagation()} // Prevent touch events from reaching overlay
      >
        {loadingMore ? (
          <CircularProgress size={24} />
        ) : (
          <Button 
            variant="outlined" 
            size="small" 
            onClick={handleClick}
            onTouchEnd={handleClick}
            disabled={!hasMore}
          >
            {hasMore 
              ? t('history.loadMore')
              : t('history.allLoaded')}
          </Button>
        )}
      </Box>
    );
  };

  // Updated handleLoadMore to use Redux server-side pagination with token refresh
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !user?.id) return;
    
    try {
      // Get a fresh token for pagination
      const validToken = await dispatch(getValidToken()).unwrap();
      
      await dispatch(fetchMoreConversations({
        userToken: validToken,
        pageSize: 20 // Load 20 more conversations
      })).unwrap();
    } catch (error) {
      const errorMessage = handleTokenError(error, {
        operation: 'conversation pagination',
        logError: true
      });
      
      // TODO: Add user notification system for pagination errors
    }
  }, [dispatch, loadingMore, hasMore, user?.id]);

  // Render loading spinner during initial load
  const renderLoadingState = useCallback(() => {
    return (
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}
      >
        <CircularProgress size={32} sx={{ color: 'text.disabled' }} />
      </Box>
    );
  }, []);

  // Render elegant empty state for new users
  const renderEmptyState = useCallback(() => {
    return (
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: { xs: 3, sm: 4 },
          textAlign: 'center'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.disabled',
            fontSize: '0.9rem',
            fontWeight: 400,
            lineHeight: 1.6
          }}
        >
          {t('history.noHistory')}
        </Typography>
      </Box>
    );
  }, [t]);

  return (
    <>
      <Box 
        className={`${styles.historyPanel} ${isHistoryOpenMobile ? styles.open : ''} ${isPanelMounted ? styles.mounted : ''}`}
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100vh', 
          maxHeight: '100vh',
          overflow: 'hidden', // Prevent outer container from scrolling
          visibility: isPanelMounted ? 'visible' : 'hidden', // Hide until mounted
          opacity: isPanelMounted ? 1 : 0, // Fade in when mounted
          transition: 'opacity 0.3s ease-out' // Smooth transition
        }}
      >
        <Box className={styles.historyHeader}>
          <Typography variant="h6" component="div">
            {t('history.title')}
          </Typography>
          <IconButton
            color="primary"
            aria-label="new chat"
            onClick={() => {
              handleNewChat();
              setIsHistoryOpenMobile(false);
            }}
          >
            <MapsUgcOutlinedIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box 
          className={styles.historyList} 
          ref={setListElement}
          sx={{ 
            flex: 1,
            overflowY: 'auto',
            height: 'calc(100% - 60px)',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <List sx={{ width: '100%', padding: 0, flex: 1 }}>
            {conversations.length === 0 ? (
              loadingHistory ? renderLoadingState() : renderEmptyState()
            ) : (
              <>
                {conversations.map((conversation: Conversation) => (
                  <ListItemButton
                    key={conversation.id}
                    onClick={() => enhancedHandleSelectSession(conversation)}
                    onContextMenu={(e) => handleContextMenu(e, conversation)}
                    selected={selectedSession?.id === conversation.id}
                    className={`${styles.historyListItem} ${selectedSession?.id === conversation.id ? styles.historyListItemSelected : ''}`}
                    sx={{ position: 'relative' }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography 
                            variant="body1" 
                            noWrap 
                            component="div"
                            sx={{ 
                              flexGrow: 1, 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              paddingRight: 1,
                              maxWidth: {
                                xs: '100px', // Reduced to make space for menu
                                sm: '150px',
                                md: '200px',
                                lg: '250px'
                              }
                            }}
                          >
                            {conversation.title || 
                              conversation.messages[0]?.text || 
                              conversation.lastMessageText || 
                              t('history.unnamedConversation')}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1, flexShrink: 0 }}>
                            {/* Show Archive tag only for legacy conversations */}
                            {isLegacyConversationType(conversation.thinkingType) ? (
                              <Box 
                                component="span" 
                                sx={{ 
                                  backgroundColor: colors.midnightGreen,
                                  color: 'white',
                                  fontSize: {
                                    xs: '0.65rem',
                                    sm: '0.7rem'
                                  },
                                  padding: {
                                    xs: '1px 4px',
                                    sm: '2px 6px'
                                  },
                                  borderRadius: '4px',
                                  whiteSpace: 'nowrap',
                                  fontWeight: 'medium',
                                  maxHeight: '20px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                {t('history.archivedConversationBadge')}
                              </Box>
                            ) : null}
                            {conversation.loading && (
                              <Box 
                                component="span" 
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  flexShrink: 0
                                }}
                              >
                                <CircularProgress size={14} />
                              </Box>
                            )}
                            {/* Add conversation menu */}
                            <ConversationMenu 
                              conversationId={conversation.id}
                              thinkingType={conversation.thinkingType}
                              onDeleteConversation={handleDeleteConversation}
                            />
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 0.5 }}>
                          <Typography component="span" variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {formatRelativeDateTime(conversation.lastMessageTimestamp)}
                          </Typography>
                          <Typography component="span" variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                            {conversation.messageCount || 0}{' '}
                            {(conversation.messageCount || 0) === 1 
                              ? t('history.message') 
                              : t('history.messages')}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </>
            )}
          </List>
          
          {/* Add Load More button at the bottom - outside the scrollable list */}
          <Box 
            sx={{ padding: '8px', borderTop: '1px solid rgba(0,0,0,0.1)' }}
            onClick={(e) => e.stopPropagation()} // Prevent clicks in pagination area from closing panel
            onTouchEnd={(e) => e.stopPropagation()} // Prevent touch events from closing panel
          >
            {conversations.length > 0 && (
              <>
                <LoadMoreButton onClick={handleLoadMore} />
                
                {/* Debug info */}
                <Typography variant="caption" align="center" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.disabled' }}>
                  {t('history.showing')}: {conversations.length} {t('history.loaded')}
                  {hasMore && ` • ${t('history.moreAvailable')}`}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Context Menu (on right-click) */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          elevation: 1,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
            width: 220, // Increased width for German text
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
              borderRadius: '4px',
              mx: 0.5,
              '&:hover': {
                backgroundColor: colors.boneLight
              }
            }
          }
        }}
        onClick={(e) => e.stopPropagation()} // Stop event from closing history panel on mobile
      >
        <MenuItem onClick={handleDeleteFromContextMenu} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('history.deleteConversation')}
        </MenuItem>
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        onClick={(e) => e.stopPropagation()} // Prevent conversation selection
      >
        <DialogTitle id="delete-dialog-title">
          {t('history.deleteConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" component="div">
            {t('history.deleteConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('history.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('history.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Overlay */}
      <Box
        className={`${styles.overlay} ${isHistoryOpenMobile ? styles.open : ''}`}
        onClick={() => setIsHistoryOpenMobile(false)}
      />
    </>
  );
};

export default History;
